import React from 'react';
import { Box, CircularProgress, Dialog } from '@mui/material';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useToast } from '../../providers/toaster-provider';
import DocumentLinkSettings, { ShareLink } from './document-link-settings';
import { defaultLinkName } from '../../util/link-name';

type InvoiceDocument = {
    slug: string;
    title: string;
    accessType: 'anonymous' | 'email_list';
    expiresAt: string | null;
    canPay: boolean;
    createdAt: string;
};

type Props = {
    open: boolean;
    invoiceId: number | null;
    invoiceNumber: string;
    entityId: number | null;
    onClose: () => void;
};

// Every invoice Share button (Invoices page "Share / Permissions", and Edit on
// an invoice's share link on the Documents page) opens the same link settings
// modal as any other document link. An invoice has exactly one link - its
// document's own slug - so this finds-or-creates that document (same
// idempotent from-invoice call the old Share dialog used) and opens the modal
// on it, with Stripe payment editable and the rest locked.
const InvoiceShareLink: React.FC<Props> = ({ open, invoiceId, invoiceNumber, entityId, onClose }) => {
    const { showError } = useToast();
    const [fetchDoc, setFetchDoc] = useApi<{ success: boolean; document: InvoiceDocument }>();
    const [doc, setDoc] = React.useState<InvoiceDocument | null>(null);

    React.useEffect(() => {
        if (!open || !invoiceId) return;
        setDoc(null);
        setFetchDoc({
            ...buildApiRequestConfig(routes.documents.fromInvoice, [{ param: 'invoiceId', value: invoiceId }]),
            body: {},
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, invoiceId]);

    React.useEffect(() => {
        const { data, error } = fetchDoc;
        if (data?.document) setDoc(data.document);
        if (error) {
            showError(typeof error === 'string' ? error : 'Failed to load the invoice share link');
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchDoc.data, fetchDoc.error]);

    const link: ShareLink | null = React.useMemo(() => (doc ? {
        id: 0,
        slug: doc.slug,
        accessType: doc.accessType ?? 'anonymous',
        listMode: 'allow',
        entries: [],
        // Same default name as every other link (util/link-name.ts) - the
        // invoice's name plus when its link was generated.
        nameLabel: defaultLinkName(doc.title || `Invoice #${invoiceNumber}`, doc.createdAt),
        expiresAt: doc.expiresAt ?? null,
        allowDownloads: true,
        watermarkTemplate: null,
        watermarkOptions: null,
        hasPassword: false,
    } : null), [doc]);

    if (!open) return null;

    if (!doc || !link) {
        return (
            <Dialog open maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                </Box>
            </Dialog>
        );
    }

    return (
        <DocumentLinkSettings
            open
            documentSlug={doc.slug}
            documentTitle={doc.title || `Invoice #${invoiceNumber}`}
            isInvoice
            invoiceShareLink={{ canPay: !!doc.canPay }}
            entityId={entityId}
            link={link}
            onClose={onClose}
            onSaved={onClose}
        />
    );
};

export default InvoiceShareLink;
